import * as React from 'react';
import {
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CopyToTrain from './Actions/CopyToTrain';
import DocStepper from './DocStepper';
import IconDownload from './Actions/IconDownload';
import IconFilterValidationErrors from './Actions/IconFilterValidationErrors';
import ToolActions from './Actions/ToolActions';
import { AppContext } from '../../../App';
import SpreadsheetCorrectedNormalized from '../../spreadsheet/SpreadsheetCorrectedNormalized';
import { IResultsType } from '../../../types/spreadsheet-types';
import { IDoc, IExtraction } from '../../../types/my-types';
import { MarkDispatchContext } from '../../../context/MarkContext';
import { featureFlags } from '../../../utils/featureFlags';
import { styled } from '@mui/material/styles';
import IconDeleteExtraction from './Actions/IconDeleteExtraction';
import { useParams } from 'react-router';
import useGetExtractionBatch from '../../hooks/useGetExtractionBatch';
import IconDeleteDocument from './Actions/IconDeleteDocument';
import ImportFields from '../../fields/ImportFields';
import IconRevertBulkCorrections from './Actions/IconRevertBulkCorrections';
import IconValidationSettings from './Actions/IconValidationSettings';
import IconTogglePhrasifier from './Actions/IconTogglePhrasifier';
import IconMarkValidationSettings from './Actions/IconMarkValidationSettings';
import {
  LinkTableStyled,
  DivFlexHeaderIcons,
  DivVerticalDividerLarge,
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../../common/StyledComponents';
import DocumentDataEditStatus from '../../extractionBatches/details/DocumentDataEditStatus';
import DocumentDataStatus from '../../extractionBatches/details/DocumentDataStatus';
import { useAtom } from 'jotai';
import { documentDataEditDialogIdAtom } from '../../../atoms/review-atoms';
import { showMarkSpreadsheetAtom } from '../../../atoms/mark-atoms';
import { getIdFromUrl } from '../../../utils/getIdFromUrl';

export interface ToolNavProps {
  steps: number;
  docs: IDoc[];
  batchLabel: string;
  docLabel: string;
  docId?: string;
  docIndex: number;
  review: boolean;
  prefetchPage: () => void;
  tokenizerVersion?: string;
  extractionId?: string;
  extractions?: IExtraction[];
  trainingSetId?: string;
  // When in review, allow parent to supply spreadsheet results type and setter
  resultsType?: IResultsType;
  setResultsType?: (t: IResultsType) => void;
  isFullscreen?: boolean;
  setIsFullscreen?: (v: boolean) => void;
  isMarkValidationLoading?: boolean;
}

const LAYOUT_BREAKPOINT_REVIEW = 1230;
const LAYOUT_BREAKPOINT_MARK = 600;

const DividerStyled = styled(Divider)(() => ({
  height: '32px',
  position: 'relative',
  margin: '5px 4px 0',
}));

const DivNavContainer = styled('div')({
  display: 'flex',
  marginTop: '0',
  marginBottom: '0.4em',
});

const DivLeftSection = styled('div')<{ $review: boolean }>(({ $review }) => ({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: '8px',
  minWidth: 0,
  [`@media (max-width: ${$review ? LAYOUT_BREAKPOINT_REVIEW : LAYOUT_BREAKPOINT_MARK}px)`]: {
    flex: '0 0 auto',
  },
}));

const DivRightSection = styled('div')<{ $review: boolean }>(({ $review }) => ({
  flex: 1,
  minWidth: 0,
  [`@media (max-width: ${$review ? LAYOUT_BREAKPOINT_REVIEW : LAYOUT_BREAKPOINT_MARK}px)`]: {
    flex: '0 1 auto',
  },
}));

const DivStepperWrapper = styled('div')<{ $review: boolean }>(({ $review }) => ({
  [`@media (max-width: ${$review ? LAYOUT_BREAKPOINT_REVIEW : LAYOUT_BREAKPOINT_MARK}px)`]: {
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const DivHeaderSection = styled('div')({
  // float: 'right',
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  // marginRight: '1em',
});

const ToolNavMarkReview: React.FC<ToolNavProps> = ({
  steps,
  docs,
  batchLabel,
  docLabel,
  docId,
  docIndex,
  review,
  prefetchPage,
  tokenizerVersion,
  extractionId,
  extractions,
  trainingSetId,
  resultsType,
  setResultsType,
  isFullscreen,
  setIsFullscreen,
  isMarkValidationLoading,
}) => {
  const { dpid, id: eid } = useParams<Record<'dpid' | 'id', string | undefined>>();

  if (!dpid || !eid) {
    return null;
  }

  const { data: extractionBatch } = useGetExtractionBatch(dpid, eid, review);

  const dispatch = React.useContext<React.Dispatch<any>>(MarkDispatchContext);
  const { setToast, showReviewSpreadsheet, setShowReviewSpreadsheet } =
    React.useContext(AppContext);
  const [, setDocumentDataEditDialogId] = useAtom(documentDataEditDialogIdAtom);
  const [showMarkSpreadsheet, setShowMarkSpreadsheet] = useAtom(showMarkSpreadsheetAtom);

  // Get current extraction
  const currentExtraction = extractions?.find((ext) => ext.id === extractionId);

  const handleSwitchChange = () => {
    setShowReviewSpreadsheet(!showReviewSpreadsheet);

    // If the user is disabling the grid, set the visible extraction list to the extraction being viewed
    if (review && showReviewSpreadsheet && extractionId) {
      dispatch({
        type: 'SET_VISIBLE_EXTRACTIONS',
        payload: [extractionId],
      });
    }
  };

  const displayReviewButtons = () => (
    <DivHeaderSection>
      <div>
        <TypographyPropertiesTitleStyled>DOCUMENT ID</TypographyPropertiesTitleStyled>
        <TypographyPropertiesDataStyled>
          {docId && dpid && (
            <>
              <LinkTableStyled
                to={`/data-pools/${dpid}/documents/${docId}`}
                data-cy="page-tabs-doc-id"
              >
                {docId}
              </LinkTableStyled>
              <span
                onClick={() => {
                  navigator.clipboard.writeText(docId);
                  setToast({
                    open: true,
                    message: 'Copied!',
                    variant: 'success',
                  });
                }}
                style={{ marginLeft: '5px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              >
                <ContentCopyIcon style={{ fontSize: '12px' }} />
              </span>
            </>
          )}
        </TypographyPropertiesDataStyled>
      </div>
      <DivVerticalDividerLarge />
      <div
        onClick={() => {
          if (currentExtraction?.document_data) {
            const docDataId = getIdFromUrl(currentExtraction.document_data);
            setDocumentDataEditDialogId({
              id: docDataId,
              documentId: currentExtraction.document,
              currentStatus: currentExtraction.document_data_status,
              currentExclusionReason: currentExtraction.exclusion_reason,
              currentEscalationDetail: currentExtraction.escalation_detail,
            });
          } else {
            setToast({
              variant: 'error',
              open: true,
              message: 'Document data not available',
            });
          }
        }}
        style={{ cursor: currentExtraction?.document_data_status ? 'pointer' : 'default' }}
      >
        <Tooltip title="Document Data Status" placement="top">
          <TypographyPropertiesTitleStyled>DATA STATUS</TypographyPropertiesTitleStyled>
        </Tooltip>
        <TypographyPropertiesDataStyled data-cy="page-tabs-doc-data-status">
          <DocumentDataStatus
            status={currentExtraction?.document_data_status}
            exclusionReason={currentExtraction?.exclusion_reason}
            escalationDetail={currentExtraction?.escalation_detail}
            trailingValueAsTooltip
          />
        </TypographyPropertiesDataStyled>
      </div>
      <DivVerticalDividerLarge />
      <div>
        <DivFlexHeaderIcons>
          <IconRevertBulkCorrections />
          <IconValidationSettings extractionBatch={extractionBatch} />
          <IconFilterValidationErrors />
          {/* <DividerStyled orientation="vertical" flexItem /> */}
          <IconDownload review={review} docLabel={docLabel} tokenizerVersion={tokenizerVersion} />
          <CopyToTrain isIcon />
          {/* <DividerStyled orientation="vertical" flexItem /> */}
          <IconTogglePhrasifier />
          <IconDeleteExtraction extractionId={extractionId} extractions={extractions} />
        </DivFlexHeaderIcons>
      </div>
    </DivHeaderSection>
  );

  const displayMarkButtons = () => (
    <DivHeaderSection>
      <div>
        <TypographyPropertiesTitleStyled>DOCUMENT ID</TypographyPropertiesTitleStyled>
        <TypographyPropertiesDataStyled>
          {docId && dpid && (
            <>
              <LinkTableStyled
                to={`/data-pools/${dpid}/documents/${docId}`}
                data-cy="page-tabs-doc-id"
              >
                {docId}
              </LinkTableStyled>
              <span
                onClick={() => {
                  navigator.clipboard.writeText(docId);
                  setToast({
                    open: true,
                    message: 'Copied!',
                    variant: 'success',
                  });
                }}
                style={{ marginLeft: '5px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}
              >
                <ContentCopyIcon style={{ fontSize: '12px' }} />
              </span>
            </>
          )}
        </TypographyPropertiesDataStyled>
      </div>
      <DivVerticalDividerLarge />
      <DivFlexHeaderIcons>
        {trainingSetId && (
          <IconMarkValidationSettings
            trainingSetId={trainingSetId}
            isValidationLoading={isMarkValidationLoading}
          />
        )}
        {featureFlags.dataValidation ? (
          <IconDownload review={review} docLabel={docLabel} tokenizerVersion={tokenizerVersion} />
        ) : (
          <ToolActions review={review} docLabel={docLabel} tokenizerVersion={tokenizerVersion} />
        )}
        <ImportFields iconType="iconWhiteBg" />
        <IconDeleteDocument documentId={docId} trainingSetId={trainingSetId} documents={docs} iconType="iconWhiteBg" />
      </DivFlexHeaderIcons>
    </DivHeaderSection>
  );

  return (
    <>
      <DivNavContainer>
        <DivLeftSection $review={review}>
          {setIsFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
              <span>
                <IconButton
                  size="small"
                  color={isFullscreen ? 'primary' : 'default'}
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  data-cy="toggle-fullscreen"
                >
                  {isFullscreen ? (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 9L5 5M5 5v4M5 5h4" />
                      <path d="M15 9l4-4M19 5v4M19 5h-4" />
                      <path d="M15 15l4 4M19 19v-4M19 19h-4" />
                      <path d="M9 15l-4 4M5 19v-4M5 19h4" />
                    </svg>
                  ) : (
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
                      <path d="M16 3h3a2 2 0 0 1 2 2v3" />
                      <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
                      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
                    </svg>
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <div>
            {review ? (
              <FormControlLabel
                control={
                  <Switch
                    onChange={handleSwitchChange}
                    checked={!!showReviewSpreadsheet}
                    value={showReviewSpreadsheet}
                    color="primary"
                    size="small"
                    data-cy="switch-toggle-spreadsheet"
                  />
                }
                label="Table"
                sx={{
                  m: 0,
                  ml: 0,
                  pl: 0,
                  alignSelf: 'center',
                  '& .MuiFormControlLabel-label': { ml: 0.5, fontSize: '12px', lineHeight: 1.2 },
                }}
              />
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    onChange={() => setShowMarkSpreadsheet(!showMarkSpreadsheet)}
                    checked={!!showMarkSpreadsheet}
                    value={showMarkSpreadsheet}
                    color="primary"
                    size="small"
                    data-cy="switch-toggle-mark-spreadsheet"
                  />
                }
                label="Table"
                sx={{
                  m: 0,
                  ml: 0,
                  pl: 0,
                  alignSelf: 'center',
                  '& .MuiFormControlLabel-label': { ml: 0.5, fontSize: '12px', lineHeight: 1.2 },
                }}
              />
            )}
          </div>

          {review && showReviewSpreadsheet && resultsType && setResultsType ? (
            <SpreadsheetCorrectedNormalized
              resultsType={resultsType}
              setResultsType={setResultsType}
            />
          ) : null}
        </DivLeftSection>
        <DivStepperWrapper $review={review}>
          <DocStepper steps={steps} docIndex={docIndex} prefetchPage={prefetchPage} review={review} />
        </DivStepperWrapper>
        <DivRightSection $review={review}>
          {review ? displayReviewButtons() : displayMarkButtons()}
        </DivRightSection>
      </DivNavContainer>

      {review && <DocumentDataEditStatus />}
    </>
  );
};

export default ToolNavMarkReview;
